import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, computed, effect, inject, Injectable, Injector, Signal, signal, WritableSignal } from '@angular/core';
import {
	PLANOGRAM_CANVAS_WIDGET_DATA,
	PLANOGRAM_CANVAS_WIDGET_REF,
	PlanogramCanvasWidgetRef
} from '../planogram-edit/components/planogram-canvas-widget/planogram-canvas-widget.tokens';
import { IPlanogram } from '../models/planogram';
import { OverlayZIndexService } from './overlay-z-index.service';
import { PlanogramModes } from './planogram-modes.service';
import { clampOverlayToViewport } from './overlay-viewport.util';

export interface PlanogramCanvasOverlayOpenParams {
	overlay: Overlay;
    component: any;
	parentInjector: Injector;
	hostContainer: HTMLElement;
	planogram: IPlanogram;
	editable: boolean;
}

interface OpenedPlanogramCanvas {
	planogram: IPlanogram;
	overlay: OverlayRef;
	componentRef: ComponentRef<any>;
}

export const MIN_PANEL_WIDTH_PX: number = 240;
export const MIN_PANEL_HEIGHT_PX: number = 320;
export const LAYOUT_PADDING_PX: number = 2;
const LAYOUT_GAP_PX: number = 12;
const SCROLL_TRACK_CLASS: string = 'planogram-canvas-scroll-track';

@Injectable()
export class PlanogramCanvasOverlayService {

	private readonly container: WritableSignal<HTMLElement> = signal<HTMLElement>(null);
	readonly opened: WritableSignal<Array<OpenedPlanogramCanvas>> = signal([]);
	public readonly pinnedPlanogramIds: WritableSignal<Array<number>> = signal([]);
	private dragSlots: Array<number> | null = null;
	private _resizeObserver: ResizeObserver;
	private _windowResizeAttached: boolean = false;
	private _windowResizeFrame: number | null = null;
	private readonly _onWindowResize = (): void => {
		if (this._windowResizeFrame != null) cancelAnimationFrame(this._windowResizeFrame);

		this._windowResizeFrame = requestAnimationFrame(() => {
			this._windowResizeFrame = null;
			this.layoutPanels();
			this.opened().forEach((item: OpenedPlanogramCanvas) => {
				clampOverlayToViewport(item.overlay.overlayElement as HTMLElement);
			});
		});
	};

    private readonly overlayZIndexService: OverlayZIndexService = inject(OverlayZIndexService);
	private readonly modes: PlanogramModes = inject(PlanogramModes);

	public readonly hasOpened: Signal<boolean> = computed(() => this.opened().length > 0);

	constructor() {
		effect(() => {
			this.opened();
			this.pinnedPlanogramIds();
			if (this.container() && this.hasOpened()) this.layoutPanels();
		});
	}

	public isOpened(planogram: IPlanogram): boolean {
		return this.opened().some((item: OpenedPlanogramCanvas) => item.planogram.main_stand === planogram.main_stand);
	}

	public getCanvasZoom(planogram: IPlanogram): number {
		const item: OpenedPlanogramCanvas | undefined = this.opened().find(
			(entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === planogram.main_stand
		);

		return item?.componentRef?.instance?.getCanvasZoom?.() ?? 100;
	}

	public applyCanvasZoom(planogram: IPlanogram, value: number): void {
		const item: OpenedPlanogramCanvas | undefined = this.opened().find(
			(entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === planogram.main_stand
		);

		item?.componentRef?.instance?.zoomApply?.(value);
	}

	public getCanvasImage(planogram: IPlanogram): string {
		const item: OpenedPlanogramCanvas | undefined = this.opened().find(
			(entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === planogram.main_stand
		);

		return item?.componentRef?.instance?.getCanvasImage?.() ?? '';
	}

	public getImagesMode(planogram: IPlanogram): boolean | null {
		const item: OpenedPlanogramCanvas | undefined = this.opened().find(
			(entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === planogram.main_stand
		);
		const imagesMode: boolean | undefined = item?.componentRef?.instance?.planogramCanvas?.imagesMode?.();

		return imagesMode ?? null;
	}

	public getPosMaterialsMode(planogram: IPlanogram): boolean | null {
		const item: OpenedPlanogramCanvas | undefined = this.opened().find(
			(entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === planogram.main_stand
		);
		const posMaterialsMode: boolean | undefined = item?.componentRef?.instance?.planogramCanvas?.posMaterialsMode?.();

		return posMaterialsMode ?? null;
	}

	public getSequenceNumberMode(planogram: IPlanogram): boolean | null {
		const item: OpenedPlanogramCanvas | undefined = this.opened().find(
			(entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === planogram.main_stand
		);
		const sequenceNumberMode: boolean | undefined = item?.componentRef?.instance?.planogramCanvas?.sequenceNumberMode?.();

		return sequenceNumberMode ?? null;
	}

	public getTransparentBoxesMode(planogram: IPlanogram): boolean | null {
		const item: OpenedPlanogramCanvas | undefined = this.opened().find(
			(entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === planogram.main_stand
		);
		const transparentBoxesMode: boolean | undefined = item?.componentRef?.instance?.planogramCanvas?.transparentBoxesMode?.();

		return transparentBoxesMode ?? null;
	}

	public getSizesMode(planogram: IPlanogram): boolean | null {
		const item: OpenedPlanogramCanvas | undefined = this.opened().find(
			(entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === planogram.main_stand
		);
		const sizesMode: boolean | undefined = item?.componentRef?.instance?.planogramCanvas?.sizesMode?.();

		return sizesMode ?? null;
	}

	public setContainer(container: HTMLElement | null): void {
		this.container.set(container);
		this._resizeObserver?.disconnect();
		this._resizeObserver = null;

		if (!container) return;

		this.ensureScrollTrack(container);
		this._resizeObserver = new ResizeObserver(() => this.layoutPanels());
		this._resizeObserver.observe(container);
	}

	public open(params: PlanogramCanvasOverlayOpenParams): OverlayRef | undefined {
		if (this.modes.layoutRulesLocked()) return;

		const overlayRef: OverlayRef = params.overlay.create({
			positionStrategy: params.overlay.position().global(),
			hasBackdrop: false,
			panelClass: 'planogram-canvas-widget-overlay',
			scrollStrategy: params.overlay.scrollStrategies.noop()
		});

		const mainStandId: number = params.planogram.main_stand;
		const widgetRef: PlanogramCanvasWidgetRef = {
			focus: () => {
                this.overlayZIndexService.setZIndex(overlayRef);
                params.hostContainer.focus();
            },
			close: () => this.close(overlayRef)
		};
		const injector: Injector = Injector.create({
			parent: params.parentInjector,
			providers: [
				{ provide: PLANOGRAM_CANVAS_WIDGET_DATA, useValue: {
					planogram: params.planogram,
					hostContainer: params.hostContainer,
					overlayRef: overlayRef,
					editable: params.editable
				} },
				{ provide: PLANOGRAM_CANVAS_WIDGET_REF, useValue: widgetRef }
			]
		});
		const portal: ComponentPortal<any> = new ComponentPortal(
			params.component,
			null,
			injector
		);

		const componentRef: ComponentRef<any> = overlayRef.attach(portal);
		this.ensureScrollTrack(params.hostContainer).appendChild(overlayRef.overlayElement);

		this.opened.update((items: Array<OpenedPlanogramCanvas>) => [
			...items,
			{ planogram: params.planogram, overlay: overlayRef, componentRef }
		]);
		this.pinnedPlanogramIds.update((ids: Array<number>) => [...ids, mainStandId]);
		this.overlayZIndexService.setZIndex(overlayRef);
		this.attachWindowResizeListener();

		return overlayRef;
	}

	public close(overlayRef: OverlayRef): void {
		if (this.modes.layoutRulesLocked()) return;

		const item: OpenedPlanogramCanvas = this.opened().find((item: OpenedPlanogramCanvas) => item.overlay === overlayRef);
		if (!item) return;

		this.opened.update((items: Array<OpenedPlanogramCanvas>) =>
			items.filter((entry: OpenedPlanogramCanvas) => entry.overlay !== overlayRef)
		);
		this.pinnedPlanogramIds.update((ids: Array<number>) => ids.filter((id: number) => id !== item.planogram.main_stand));
		overlayRef.dispose();

		if (!this.hasOpened()) {
			this.overlayZIndexService.reset();
			this.removeScrollTrack();
		}
		this.detachWindowResizeListenerIfEmpty();
	}

	public closeAll(): void {
		if (!this.hasOpened()) return;

		this.removeScrollTrack();

		this.opened().forEach((item: OpenedPlanogramCanvas) => item.overlay.dispose());
		this.opened.set([]);
		this.pinnedPlanogramIds.set([]);
		this.dragSlots = null;
		this._resizeObserver?.disconnect();
		this._resizeObserver = null;
		this.container.set(null);
		this.detachWindowResizeListenerIfEmpty();
	}

	public beginPinnedDrag(): void {
		this.dragSlots = this.getPinnedItems().map((item: OpenedPlanogramCanvas) => item.planogram.main_stand);
	}

	public movePinnedSlot(mainStandId: number, centerX: number): void {
		if (!this.dragSlots?.length) return;

		const layout = this.getPanelLayout(this.dragSlots.length);
		if (!layout) return;

		const itemsById: Map<number, OpenedPlanogramCanvas> = new Map(
			this.getPinnedItems().map((item: OpenedPlanogramCanvas) => [item.planogram.main_stand, item])
		);
		let slotIndex: number = this.dragSlots.indexOf(mainStandId);
		if (slotIndex < 0) return;

		const slotLeft = (index: number): number => LAYOUT_PADDING_PX + index * layout.panelStep;
		const slotCenter = (index: number): number => slotLeft(index) + layout.panelWidth / 2;
		const slotTop: number = LAYOUT_PADDING_PX;
		let swapped: boolean = false;

		if (slotIndex < this.dragSlots.length - 1) {
			const midpoint: number = (slotCenter(slotIndex) + slotCenter(slotIndex + 1)) / 2;

			if (centerX > midpoint) {
				[this.dragSlots[slotIndex], this.dragSlots[slotIndex + 1]] = [
					this.dragSlots[slotIndex + 1],
					this.dragSlots[slotIndex]
				];
				slotIndex += 1;
				swapped = true;
			}
		}

		if (!swapped && slotIndex > 0) {
			const midpoint: number = (slotCenter(slotIndex - 1) + slotCenter(slotIndex)) / 2;

			if (centerX < midpoint) {
				[this.dragSlots[slotIndex], this.dragSlots[slotIndex - 1]] = [
					this.dragSlots[slotIndex - 1],
					this.dragSlots[slotIndex]
				];
				slotIndex -= 1;
			}
		}

		this.dragSlots.forEach((id: number, index: number) => {
			if (id === mainStandId) return;

			const overlayEl: HTMLElement = itemsById.get(id)!.overlay.overlayElement as HTMLElement;
			overlayEl.style.position = 'absolute';
			overlayEl.style.left = `${slotLeft(index)}px`;
			overlayEl.style.top = `${slotTop}px`;
		});
	}

	public commitPinnedDrag(): void {
		if (this.dragSlots) {
			const itemsById: Map<number, OpenedPlanogramCanvas> = new Map(
				this.getPinnedItems().map((item: OpenedPlanogramCanvas) => [item.planogram.main_stand, item])
			);
			const order: Array<OpenedPlanogramCanvas> = this.dragSlots
				.map((id: number) => itemsById.get(id))
				.filter((item: OpenedPlanogramCanvas | undefined): item is OpenedPlanogramCanvas => !!item);
			const pinnedIdSet: Set<number> = new Set(this.pinnedPlanogramIds());
			let pinnedIndex: number = 0;

			this.opened.set(this.opened().map((entry: OpenedPlanogramCanvas) =>
				pinnedIdSet.has(entry.planogram.main_stand) ? order[pinnedIndex++] : entry
			));
			this.pinnedPlanogramIds.set(this.dragSlots);
		}

		this.dragSlots = null;
		this.layoutPanels();
	}

	public cancelPinnedDrag(): void {
		this.dragSlots = null;
		this.layoutPanels();
	}

	private getArrangedOrder(
		openedItems: Array<OpenedPlanogramCanvas>,
		mainStandIdsInOrder: Array<number>
	): Array<number> {
		const openedIdSet: Set<number> = new Set(openedItems.map((item: OpenedPlanogramCanvas) => item.planogram.main_stand));
		const orderedIds: Array<number> = mainStandIdsInOrder.filter((id: number) => openedIdSet.has(id));
		const unorderedOpenedIds: Array<number> = openedItems
			.map((item: OpenedPlanogramCanvas) => item.planogram.main_stand)
			.filter((id: number) => !orderedIds.includes(id));

		return [...orderedIds, ...unorderedOpenedIds];
	}

	public isOpenedPlanogramsInDefaultArrangement(mainStandIdsInOrder: Array<number>): boolean {
		const openedItems: Array<OpenedPlanogramCanvas> = this.opened();
		if (openedItems.length < 2) return false;

		const openedIds: Array<number> = openedItems.map((item: OpenedPlanogramCanvas) => item.planogram.main_stand);
		const pinnedIds: Array<number> = this.pinnedPlanogramIds();
		const expectedIds: Array<number> = this.getArrangedOrder(openedItems, mainStandIdsInOrder);

		if (pinnedIds.length !== openedIds.length) return false;
		if (!openedIds.every((id: number) => pinnedIds.includes(id))) return false;

		return openedIds.every((id: number, index: number) => id === expectedIds[index])
			&& pinnedIds.every((id: number, index: number) => id === expectedIds[index]);
	}

	public arrangeOpenedPlanograms(mainStandIdsInOrder: Array<number>): void {
		const openedItems: Array<OpenedPlanogramCanvas> = this.opened();
		if (openedItems.length < 2) return;
		if (this.isOpenedPlanogramsInDefaultArrangement(mainStandIdsInOrder)) return;

		const pinnedIds: Array<number> = this.getArrangedOrder(openedItems, mainStandIdsInOrder);
		const itemsById: Map<number, OpenedPlanogramCanvas> = new Map(
			openedItems.map((item: OpenedPlanogramCanvas) => [item.planogram.main_stand, item])
		);

		this.dragSlots = null;
		this.pinnedPlanogramIds.set(pinnedIds);
		this.opened.set(pinnedIds.map((id: number) => itemsById.get(id)!));
		this.layoutPanels();
	}

	public setPinned(mainStandId: number, pinned: boolean): void {
		if (this.modes.layoutRulesLocked()) return;

		const item: OpenedPlanogramCanvas | undefined = this.opened()
			.find((entry: OpenedPlanogramCanvas) => entry.planogram.main_stand === mainStandId);

		if (!item) return;

		const overlayEl: HTMLElement = item.overlay.overlayElement as HTMLElement;

		if (!pinned) {
			const rect: DOMRect = overlayEl.getBoundingClientRect();

			this.pinnedPlanogramIds.update((ids: Array<number>) => ids.filter((id: number) => id !== mainStandId));
			overlayEl.classList.remove('is-pinned');
			overlayEl.style.position = 'fixed';
			overlayEl.style.left = `${rect.left}px`;
			overlayEl.style.top = `${rect.top}px`;
			overlayEl.style.width = `${rect.width}px`;
			overlayEl.style.height = `${rect.height}px`;

			return;
		}

		if (this.pinnedPlanogramIds().includes(mainStandId)) return;

		this.opened.update((items: Array<OpenedPlanogramCanvas>) => [
			...items.filter((entry: OpenedPlanogramCanvas) => entry.planogram.main_stand !== mainStandId),
			item
		]);
		this.pinnedPlanogramIds.update((ids: Array<number>) => [...ids, mainStandId]);
		this.overlayZIndexService.setZIndex(item.overlay);
	}

	private getPinnedItems(): Array<OpenedPlanogramCanvas> {
		const pinnedIdSet: Set<number> = new Set(this.pinnedPlanogramIds());

		return this.opened().filter((item: OpenedPlanogramCanvas) => pinnedIdSet.has(item.planogram.main_stand));
	}

	private getPanelLayout(panelCount: number) {
		const container: HTMLElement | null = this.container();
		if (!container || !panelCount) return null;

		const containerWidth: number = container.clientWidth || container.offsetWidth;
		const containerHeight: number = container.clientHeight || container.offsetHeight;
		if (!containerWidth || !containerHeight) return null;

		const innerWidth: number = containerWidth - LAYOUT_PADDING_PX * 2;
		const innerHeight: number = containerHeight - LAYOUT_PADDING_PX * 2;
		const gaps: number = panelCount > 1 ? (panelCount - 1) * LAYOUT_GAP_PX : 0;
		const panelWidth: number = panelCount === 1
			? innerWidth
			: panelCount * MIN_PANEL_WIDTH_PX + gaps <= innerWidth
				? (innerWidth - gaps) / panelCount
				: MIN_PANEL_WIDTH_PX;

		return {
			panelWidth,
			panelStep: panelCount === 1 ? panelWidth : panelWidth + LAYOUT_GAP_PX,
			panelHeight: innerHeight,
			contentWidth: LAYOUT_PADDING_PX * 2 + panelCount * panelWidth + gaps
		};
	}

	public getPinnedDragMaxLeft(panelWidth: number): number {
		const layout = this.getPanelLayout(this.getPinnedItems().length);
		if (!layout) return LAYOUT_PADDING_PX;

		return layout.contentWidth - LAYOUT_PADDING_PX - panelWidth;
	}

	public layoutPanels(): void {
		const pinned: Array<OpenedPlanogramCanvas> = this.getPinnedItems();
		const container: HTMLElement | null = this.container();
		const scrollTrack: HTMLElement | null = container
			? container.querySelector(`:scope > .${SCROLL_TRACK_CLASS}`)
			: null;

		if (!pinned.length) {
			if (scrollTrack) {
				scrollTrack.style.minWidth = '';
				scrollTrack.style.minHeight = '';
			}
			return;
		}

		const layout = this.getPanelLayout(pinned.length);
		if (!layout) return;

		if (scrollTrack && container) {
			const needsScroll: boolean = layout.contentWidth > container.clientWidth;
			scrollTrack.style.minWidth = needsScroll ? `${layout.contentWidth}px` : '';
			scrollTrack.style.minHeight = `${container.clientHeight}px`;
		}

		pinned.forEach((item: OpenedPlanogramCanvas, index: number) => {
			const overlayEl: HTMLElement = item.overlay.overlayElement as HTMLElement;

			overlayEl.classList.add('is-pinned');
			overlayEl.style.position = 'absolute';
			overlayEl.style.left = `${LAYOUT_PADDING_PX + index * layout.panelStep}px`;
			overlayEl.style.top = `${LAYOUT_PADDING_PX}px`;
			overlayEl.style.width = `${layout.panelWidth}px`;
			overlayEl.style.height = `${layout.panelHeight}px`;
			overlayEl.style.right = 'auto';
			overlayEl.style.bottom = 'auto';
			overlayEl.style.transform = 'none';
			overlayEl.style.zIndex = `${index + 1}`;
		});
	}

	public scrollToPlanogram(mainStandId: number): void {
		requestAnimationFrame(() => {
            const container: HTMLElement | null = this.container();
            if (!container) return;

            const pinnedIds: Array<number> = this.pinnedPlanogramIds();
            const index: number = pinnedIds.indexOf(mainStandId);
            if (index < 0) return;

            const layout = this.getPanelLayout(pinnedIds.length);
            if (!layout) return;

            const panelLeft: number = LAYOUT_PADDING_PX + index * layout.panelStep;
            const panelRight: number = panelLeft + layout.panelWidth;
            const viewportLeft: number = container.scrollLeft;
            const viewportRight: number = viewportLeft + container.clientWidth;
            let targetScrollLeft: number = viewportLeft;

            if (panelLeft < viewportLeft) {
                targetScrollLeft = panelLeft;
            } else if (panelRight > viewportRight) {
                targetScrollLeft = panelRight - container.clientWidth;
            } else {
                return;
            }

            const maxScrollLeft: number = container.scrollWidth - container.clientWidth;

            container.scrollTo({
                left: Math.max(0, Math.min(targetScrollLeft, maxScrollLeft)),
                behavior: 'smooth'
            });
        });
	}

	private ensureScrollTrack(container: HTMLElement): HTMLElement {
		let track: HTMLElement | null = container.querySelector(`:scope > .${SCROLL_TRACK_CLASS}`);

		if (!track) {
			track = document.createElement('div');
			track.className = SCROLL_TRACK_CLASS;
			container.appendChild(track);
		}

		container.querySelectorAll(':scope > .planogram-canvas-widget-overlay').forEach((overlayEl: Element) => {
			track!.appendChild(overlayEl);
		});

		return track;
	}

	private removeScrollTrack(): void {
		const container: HTMLElement | null = this.container();
		container?.querySelector(`:scope > .${SCROLL_TRACK_CLASS}`)?.remove();
	}

	private attachWindowResizeListener(): void {
		if (this._windowResizeAttached) return;

		window.addEventListener('resize', this._onWindowResize);
		this._windowResizeAttached = true;
	}

	private detachWindowResizeListenerIfEmpty(): void {
		if (this.hasOpened() || !this._windowResizeAttached) return;

		window.removeEventListener('resize', this._onWindowResize);
		this._windowResizeAttached = false;

		if (this._windowResizeFrame != null) {
			cancelAnimationFrame(this._windowResizeFrame);
			this._windowResizeFrame = null;
		}
	}
}
